import {
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { MyAbsenceItem } from '../types';
import { widgetTokens } from '../designTokens';
import { DashboardWidget } from './DashboardWidget';
import { WidgetMoreLink } from './WidgetMoreLink';

interface MyAbsencesWidgetProps {
  items?: MyAbsenceItem[];
  loading?: boolean;
}

function statusColor(
  status: string,
): 'default' | 'info' | 'success' | 'warning' {
  const normalised = status.toUpperCase();
  if (normalised.includes('APPROVED')) return 'success';
  if (normalised.includes('PENDING')) return 'warning';
  if (normalised.includes('CANCEL') || normalised.includes('REJECT')) {
    return 'default';
  }
  return 'info';
}

export function MyAbsencesWidget({ items, loading }: MyAbsencesWidgetProps) {
  const empty = !loading && (!items || items.length === 0);
  const all = items ?? [];
  const visible = all.slice(0, widgetTokens.maxRows);
  const hidden = Math.max(0, all.length - visible.length);

  return (
    <DashboardWidget
      title="My time off"
      icon={<FlightTakeoffIcon fontSize="small" />}
      iconBg="secondary.50"
      accentColor="secondary.main"
      loading={loading}
      empty={empty}
      emptyMessage="No upcoming absences in the next 30 days."
      data-testid="widget-my-absences"
    >
      <List dense disablePadding>
        {visible.map((item) => (
          <ListItem key={item.id} disableGutters>
            <ListItemText
              primary={
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {item.type ?? 'Absence'}
                  </Typography>
                  <Chip
                    size="small"
                    color={statusColor(item.status)}
                    variant="outlined"
                    label={item.status}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                </Stack>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.startDate).toLocaleDateString()} –{' '}
                  {new Date(item.endDate).toLocaleDateString()}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
      <WidgetMoreLink hiddenCount={hidden} />
    </DashboardWidget>
  );
}
